import React, { useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { Button } from '../ui/Button';
import { CheckIcon, Edit2Icon, SendIcon, DownloadIcon, Loader2Icon, ShieldCheckIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
import { MascotPanel } from '../ui/MascotPanel';
import { generateIntakeHTML } from '../../utils/generateIntakeHTML';
import { generateIntakeMarkdown } from '../../utils/generateIntakeMarkdown';
import { submitIntake } from '../../utils/submitIntake';
import { getRelevantUploads } from '../../utils/buildIntakeReport';
import { websiteLanguagesOptions, integrationOptions, yesNoUnknownOptions, mainGoalOptions, websiteTypeOptions, seoImportanceOptions, launchDateOptions, pageOptions, textContentOptions, typographyOptions } from '../../data/intakeOptions';
/** Download the AI-friendly Markdown version of the intake as a .md file */
function downloadMarkdown(markdown: string, clientName: string) {
  const blob = new Blob([markdown], {
    type: 'text/markdown;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `intake-${clientName.replace(/\s+/g, '-').toLowerCase()}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
/** Detect iOS / iPadOS (including iPad in desktop mode) */
function isAppleDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPad in desktop mode reports as Mac but has touch support
  navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}
/**
 * Download or open the HTML intake so the user can save / print it as PDF.
 * – Desktop : opens a new window and triggers the browser print dialog.
 * – Apple   : opens the file as a data-URL in the current tab so iOS Safari
 *             shows the share sheet → "Print" or "Save to Files".
 */
function downloadOrPrintPDF(htmlContent: string, clientName: string) {
  if (isAppleDevice()) {
    // iOS Safari doesn't support window.print() in pop-ups.
    // Opening a data-URL shows the native share/print sheet.
    const encoded = encodeURIComponent(htmlContent);
    const dataUrl = `data:text/html;charset=utf-8,${encoded}`;
    window.location.href = dataUrl;
  } else {
    // Desktop & Android: open in new window and trigger print dialog.
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      // Pop-ups blocked – fall back to blob download.
      const blob = new Blob([htmlContent], {
        type: 'text/html'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intake-noord-digital-${clientName.replace(/\s+/g, '-').toLowerCase()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
}
export function Step15Summary() {
  const {
    data,
    setCurrentStep,
    uploadedFiles
  } = useIntake();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const SummarySection = ({
    title,
    step,
    children
  }: {
    title: string;
    step: number;
    children: React.ReactNode;
  }) => <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6" data-id="element-221" data-mp="i1hsme3g6ebw">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-222" data-mp="1ohljd2sx1j3o">
        <h3 className="font-bold text-gray-900" data-id="element-223" data-mp="1teu1x4hz7p5g">{title}</h3>
        <button onClick={() => setCurrentStep(step)} className="text-sm font-medium text-[#007AFF] hover:text-[#005FCC] flex items-center" data-id="element-224" data-mp="1r18fimllg1jx">
          <Edit2Icon className="w-4 h-4 mr-1" data-id="element-225" data-mp="1n1fzxpxs8v52" />
          Bewerken
        </button>
      </div>
      <div className="p-6 space-y-2" data-id="element-226" data-mp="210pgiqpwhr9k">{children}</div>
    </div>;
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => <div className="flex flex-col sm:flex-row sm:py-2 border-b border-gray-100 last:border-0" data-id="element-227" data-mp="29yroxrbyrm6v">
      <span className="text-sm font-medium text-gray-500 sm:w-1/3 mb-1 sm:mb-0" data-id="element-228" data-mp="25xpnr4yhl029">
        {label}
      </span>
      <span className="text-sm text-gray-900 sm:w-2/3 font-medium" data-id="element-229" data-mp="2jpdyb3t37gh4">
        {value || <span className="text-gray-400 italic" data-id="element-230" data-mp="2eru9j9yrjlax">Niet ingevuld</span>}
      </span>
    </div>;
  const htmlContent = generateIntakeHTML(data, uploadedFiles);
  const markdownContent = generateIntakeMarkdown(data, uploadedFiles);
  const attachments = getRelevantUploads(data, uploadedFiles).map(u => u.file);
  const languagesSummary = data.websiteLanguages === 'more' ? data.websiteLanguagesOther || 'Meer talen' : websiteLanguagesOptions[data.websiteLanguages] || '';
  const integrationsSummary = data.integrations.map(v => v === 'other' ? data.integrationsOther || 'Anders' : integrationOptions[v] || v).join(', ');
  const deadlineSummary = data.hardDeadline === 'yes' ? `Ja${data.deadlineReason ? ` — ${data.deadlineReason}` : ''}` : data.hardDeadline === 'no' ? 'Nee' : '';
  const clientName = data.companyName || data.contactName || 'Nieuwe Klant';
  const handleSubmit = async () => {
    if (!acceptedPrivacy) {
      alert('Ga akkoord met de privacyvoorwaarden om verder te gaan.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    const clientEmail = data.contactEmail || 'klant@voorbeeld.nl';
    const result = await submitIntake(htmlContent, markdownContent, clientEmail, clientName, attachments);
    setIsSubmitting(false);
    if (result.success) {
      setIsSuccess(true);
    } else {
      setSubmitError(result.error || 'Er is een fout opgetreden bij het verzenden. Probeer het opnieuw of neem contact op met info@noorddigital.nl.');
    }
  };
  const handleDownloadPDF = () => downloadOrPrintPDF(htmlContent, clientName);
  const handleDownloadMarkdown = () => downloadMarkdown(markdownContent, clientName);
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="max-w-2xl mx-auto text-center py-12" data-id="element-231" data-mp="25g3e6380gugm">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-232" data-mp="p3off0pzjm07">
          <CheckIcon className="w-12 h-12 text-green-600" data-id="element-233" data-mp="k4bqmafk04uz" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-234" data-mp="k768pvb3hdl8">
          Intake Succesvol Verzonden!
        </h2>
        <p className="text-lg text-gray-600 mb-8" data-id="element-235" data-mp="30r087wn6r7zi">
          Bedankt voor uw tijd, {data.contactName}. We hebben uw gegevens in
          goede orde ontvangen op info@noorddigital.nl en een kopie naar{' '}
          {data.contactEmail} gestuurd. Wij nemen spoedig contact met u op.
        </p>

        <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100" data-id="element-236" data-mp="3fgfy7aew79e5">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-237" data-mp="3fi8xg4n6erc4">
            Bewaar een kopie voor uzelf
          </h3>
          <p className="text-sm text-gray-600 mb-6" data-id="element-238" data-mp="3ogulgfbf2yka">
            U kunt uw volledige intake inclusief alle antwoorden downloaden als
            PDF voor uw eigen administratie.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-239" data-mp="3tdl1j2n81u5p">
            <Button onClick={handleDownloadPDF} size="lg" className="shadow-md" data-id="element-240" data-mp="1u4nxsh1c6pb3">
              <DownloadIcon className="w-5 h-5 mr-2" data-id="element-241" data-mp="1z2pfoyn10ikz" />
              {isAppleDevice() ? 'Intake Openen / Opslaan' : 'Download als PDF'}
            </Button>
            <Button variant="outline" size="lg" onClick={handleDownloadMarkdown} data-id="element-242" data-mp="3vzbz8xqbszj2">
              <FileTextIcon className="w-5 h-5 mr-2" data-id="element-243" data-mp="elyed12u5bn6" />
              Download als .md
            </Button>
          </div>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-4xl mx-auto space-y-8" data-id="element-244" data-mp="3i1dl9tfx3543">
      <MascotPanel message="Geweldig, we zijn er! Controleer hieronder uw gegevens. Als alles klopt, kunt u de intake definitief indienen. U kunt daarna alles downloaden als PDF." data-id="element-245" data-mp="3mzov4467pfmb" />

      {submitError && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl" data-id="element-246" data-mp="129q1kzpwz938">
          <div className="flex items-start" data-id="element-247" data-mp="1etdblpxozfga">
            <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-248" data-mp="10ssquo87v9k5" />
            <div data-id="element-249" data-mp="14tmdle9xaibg">
              <h3 className="text-sm font-semibold text-red-800 mb-1" data-id="element-250" data-mp="14t49xwkxl1ti">
                Verzenden mislukt
              </h3>
              <p className="text-sm text-red-700" data-id="element-251" data-mp="1skrs9q44n4k6">{submitError}</p>
              <p className="text-sm text-red-700 mt-2" data-id="element-252" data-mp="1xj31bymllb1c">
                U kunt de intake ook downloaden als PDF en handmatig opsturen
                naar{' '}
                <a href="mailto:info@noorddigital.nl" className="underline font-semibold" data-id="element-253" data-mp="1jp2cpxpd14ep">
                  info@noorddigital.nl
                </a>
              </p>
            </div>
          </div>
        </motion.div>}

      <div className="space-y-6" data-id="element-254" data-mp="2gam88gjqt98n">
        <SummarySection title="Contact & Bedrijf" step={2} data-id="element-255" data-mp="2kmjm65980yhq">
          <DataRow label="Naam" value={data.contactName} data-id="element-256" data-mp="2kn2tks18w4hi" />
          <DataRow label="Functie" value={data.contactRole} data-id="element-257" data-mp="2pm606gwviirl" />
          <DataRow label="E-mail" value={data.contactEmail} data-id="element-258" data-mp="2pjjqobqf9t6q" />
          <DataRow label="Telefoon" value={data.contactPhone} data-id="element-259" data-mp="2ankbkh4wass3" />
          <DataRow label="Bedrijfsnaam" value={data.companyName} data-id="element-260" data-mp="2fn6qqw4aaj3j" />
          <DataRow label="KVK Nummer" value={data.cocNumber} data-id="element-261" data-mp="2fpjfbs4jrrpk" />
          <DataRow label="Talen website" value={languagesSummary} data-id="element-262" data-mp="21v0j5yf75kfp" />
          {data.multipleLocations === 'yes' ? <DataRow label="Aantal locaties" value={`${data.locations.length} locaties opgegeven`} data-id="element-263" data-mp="26obmhpksvj2v" /> : <DataRow label="Adres" value={`${data.mainStreet}, ${data.mainCity}`} data-id="element-264" data-mp="1wwfla5nf3cuf" />}
        </SummarySection>

        <SummarySection title="Verhaal & Branding" step={5} data-id="element-265" data-mp="1izage8zfwk0r">
          <DataRow label="Diensten" value={data.services.length > 0 ? data.services.map(s => s.name).filter(Boolean).join(', ') || `${data.services.length} dienst(en) opgegeven` : ''} data-id="element-266" data-mp="1mxhn0b06w4y8" />
          <DataRow label="Kernwaarden" value={data.coreValues} data-id="element-267" data-mp="18cc4y33by8v7" />
          <DataRow label="Ideale website" value={data.idealWebsiteDescription} data-id="element-268" data-mp="18e6szq9weh3i" />
          <DataRow label="Huisstijl aanwezig" value={data.hasIdentity === 'yes_complete' ? 'Ja, compleet' : data.hasIdentity === 'yes_partly' ? 'Ja, deels' : 'Nee'} data-id="element-269" data-mp="1n7099dwzv198" />
          <DataRow label="Typografie wens" value={typographyOptions[data.typographyChoice] || ''} data-id="element-270" data-mp="1s2flk1fcsp3j" />
          <DataRow label="Merkkleuren" value={data.preferredColors.length > 0 ? data.preferredColors.map(c => c.name).join(', ') : ''} data-id="element-271" data-mp="2119m22vt1zmd" />
        </SummarySection>

        <SummarySection title="Website Doel & Structuur" step={8} data-id="element-272" data-mp="12muagp6gt5w3">
          <DataRow label="Primair doel" value={data.mainGoal === 'other' ? data.mainGoalOther : mainGoalOptions[data.mainGoal] || ''} data-id="element-273" data-mp="17n8d1rparnmq" />
          <DataRow label="Type website" value={websiteTypeOptions[data.websiteType] || ''} data-id="element-274" data-mp="1hfxnqkd0wxh0" />
          <DataRow label="Inspiratie websites" value={data.inspirationWebsites} data-id="element-275" data-mp="1qerf9wbhyxnp" />
          <DataRow label="Gewenste indruk" value={data.desiredImpression} data-id="element-276" data-mp="1vduj5dfqqxu3" />
          <DataRow label="Gewenste pagina's" value={data.neededPages.map(p => p === 'other' ? data.customPageOther : pageOptions[p] || p).filter(Boolean).join(', ')} data-id="element-277" data-mp="3s64gaaacgleh" />
          <DataRow label="Teksten" value={textContentOptions[data.existingContent[0]] || ''} data-id="element-278" data-mp="3pn9ha5qww8qm" />
        </SummarySection>

        <SummarySection title="Techniek, SEO & Planning" step={12} data-id="element-279" data-mp="3fwojulmz5a9a">
          <DataRow label="Domeinnaam" value={data.domainOwned === 'yes' ? data.domainName : 'Moet nog geregistreerd worden'} data-id="element-280" data-mp="3awtuimiuksqc" />
          {data.domainOwned === 'yes' && <DataRow label="E-mail op domein" value={yesNoUnknownOptions[data.emailsLinked] || ''} data-id="element-281" data-mp="7dqqkl143upy" />}
          <DataRow label="Koppelingen" value={integrationsSummary} data-id="element-282" data-mp="3ulaxmiwydtye" />
          <DataRow label="Hosting" value={data.hostingActive === 'noord' ? 'Via Noord Digital' : 'Zelf regelen'} data-id="element-283" data-mp="2n6ziu8a9la7o" />
          <DataRow label="Toon (Tone of Voice)" value={data.toneOfVoice === 'formal' ? 'Formeel' : data.toneOfVoice === 'informal' ? 'Informeel' : data.toneOfVoice === 'mix' ? 'Mix' : data.toneOfVoice === 'advise' ? 'Adviseer mij' : ''} data-id="element-284" data-mp="awxzx1mthrvi" />
          <DataRow label="Belang van SEO" value={seoImportanceOptions[data.seoServices] || ''} data-id="element-285" data-mp="tnyxbl8ahfcy" />
          <DataRow label="SEO Silo pakket" value={data.seoSiloPackage === 'silver' ? 'Zilver' : data.seoSiloPackage === 'gold' ? 'Goud' : data.seoSiloPackage === 'platinum' ? 'Platinum' : ''} data-id="element-286" data-mp="kmhlwg5tu1ul" />
          <DataRow label="Silo thema's" value={data.seoSiloThemes} data-id="element-287" data-mp="39fv3myk7vv27" />
          <DataRow label="Silo regio's" value={data.seoSiloRegions} data-id="element-288" data-mp="31tzn36sqfivg" />
          <DataRow label="Gewenste live datum" value={launchDateOptions[data.launchDate] || ''} data-id="element-289" data-mp="2wvpriv33ig9b" />
          {data.launchDate && data.launchDate !== 'relaxed' && <DataRow label="Harde deadline" value={deadlineSummary} data-id="element-290" data-mp="36tvs99d61gcl" />}
        </SummarySection>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-gray-200 mt-12 shadow-sm" data-id="element-291" data-mp="2j3rs5bz6ryqc">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-292" data-mp="2e5gib18w5o84">
          Klaar om te verzenden?
        </h2>

        <div className="max-w-xl mx-auto mb-8" data-id="element-293" data-mp="2sv5xw1hqdurx">
          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors" data-id="element-294" data-mp="2svxi6g5vo62w">
            <div className="flex-shrink-0 mt-0.5" data-id="element-295" data-mp="20crxpv8x9wpo">
              <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-[#007AFF] focus:ring-[#007AFF]" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)} data-id="element-296" data-mp="20bgwa22y911q" />
            </div>
            <div data-id="element-297" data-mp="2p23affvuc7si">
              <p className="text-sm font-medium text-gray-900 flex items-center" data-id="element-298" data-mp="2tvfy9nsp4ak7">
                <ShieldCheckIcon className="w-4 h-4 mr-1 text-green-600" data-id="element-299" data-mp="2txini5m0xznv" />
                Privacy & AVG Akkoord
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-300" data-mp="224i92tpjf83h">
                Ik ga ermee akkoord dat Noord Digital mijn ingevulde gegevens
                verwerkt en opslaat conform de AVG wetgeving om contact op te
                nemen over dit project. De gegevens worden veilig verstuurd.
              </p>
            </div>
          </label>
        </div>

        <div className="text-center space-y-4" data-id="element-301" data-mp="2g1ome8rhselb">
          <Button size="lg" onClick={handleSubmit} disabled={!acceptedPrivacy || isSubmitting} className="px-12 py-4 text-lg shadow-xl w-full sm:w-auto" data-id="element-302" data-mp="3mkmtacsxyi0p">
            {isSubmitting ? <>
                <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-303" data-mp="1awhysubn2wq4" />
                Bezig met verzenden...
              </> : <>
                <SendIcon className="w-5 h-5 mr-2" data-id="element-304" data-mp="16471lz8a9p19" />
                Intake Definitief Verzenden
              </>}
          </Button>

          <div className="flex items-center justify-center gap-2" data-id="element-305" data-mp="x4tomwkyaibs">
            <span className="text-xs text-gray-400" data-id="element-306" data-mp="x6egufjslp6h">of</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-307" data-mp="s57dcsoonc5m">
            <Button variant="outline" size="lg" onClick={handleDownloadPDF} className="w-full sm:w-auto" data-id="element-308" data-mp="n646d2p56vv0">
              <DownloadIcon className="w-5 h-5 mr-2" data-id="element-309" data-mp="2hkolz0ylw17j" />
              {isAppleDevice() ? 'Openen / Opslaan als PDF' : 'Download als PDF'}
            </Button>
            <Button variant="outline" size="lg" onClick={handleDownloadMarkdown} className="w-full sm:w-auto" data-id="element-310" data-mp="2cmdf8z3ly6tj">
              <FileTextIcon className="w-5 h-5 mr-2" data-id="element-311" data-mp="1qe9hs49tbtkj" />
              Download als .md
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-4" data-id="element-312" data-mp="1cnxpa49bfssv">
            De PDF is om te lezen en te bewaren. Het .md-bestand is dezelfde
            intake in een formaat dat direct aan AI kan worden gegeven.
          </p>
        </div>
      </div>
    </motion.div>;
}